import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, resetSupabase } from '@/lib/supabase';
import type { User, AuthResponse, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Initialize auth state
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (mounted) {
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Only redirect if not on a survey response page and not logged in
        if (!session && 
            !location.pathname.startsWith('/login') && 
            !location.pathname.startsWith('/register') &&
            !location.pathname.startsWith('/logged-out') &&
            !location.pathname.startsWith('/surveys/') &&
            !location.pathname.startsWith('/campaigns/')
        ) {
          navigate('/logged-out', { replace: true });
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location]);

  const value = {
    user,
    session,
    loading,
    signIn: async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    signUp: async (email: string, password: string) => {
      const response = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      });
      if (response.error) throw response.error;
      return response;
    },
    signOut: async () => {
      try {
        // First attempt to sign out from Supabase
        const { error } = await supabase.auth.signOut();
        if (error) throw error;

        // Clear auth state
        setUser(null);
        setSession(null);
        
        // Reset the Supabase client
        await resetSupabase();
        
        // Clear any local storage items
        localStorage.clear();

        // Navigate to logged out page
        navigate('/logged-out', { replace: true });
      } catch (error) {
        console.error('Error during sign out:', error);
        // Still clear state even if API call fails
        setUser(null);
        setSession(null);
        localStorage.clear();
        navigate('/logged-out', { replace: true });
      }
    },
    updatePassword: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}